// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useState } from "react";
import { sendMailToSales } from "../../services/mail.service";
import { LuCheck, LuChevronLeft, LuChevronRight, LuX } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { hideSalesPopUp } from "../../store/auth/action";
import TalkToSalesPop from "./TalktosalesPop";

function UnSubscribedDashboard() {
  const {
    clientname,
    salesPopupContent,
    userDetails,
    dashboardClickedArea,
  } = useSelector((state) => ({
    clientname: state.clientname,
    salesPopupContent: state.salesPopupContent,
    userDetails: state.userDetails,
    dashboardClickedArea: state.dashboardClickedArea,
  }));

  const dispatch = useDispatch();
  const [talktosalessuccess, setTalkToSalesSuccess] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const salesContent = salesPopupContent?.salesContentId;
  const images = salesContent?.img || [];
  const features = salesContent?.features || [];

  function formatDateToMMDDYYYY() {
    const currentDate = new Date();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const year = String(currentDate.getFullYear());
    return `${month}-${day}-${year}`;
  }

  function createEmailSubject() {
    return `User interested in ${salesPopupContent?.label}`;
  }

  function createEmailBody() {
    const formattedDate = formatDateToMMDDYYYY();
    return `"${userDetails?.firstName} ${userDetails?.lastName}" (${userDetails?.email}) from "${clientname}" has shown interest in ${salesPopupContent?.label} by clicking on the "${dashboardClickedArea}" section on date – "${formattedDate}" (mm-dd-yyyy)`;
  }

  async function onContactSales() {
    try {
      setTalkToSalesSuccess(true);
      const subject = createEmailSubject();
      const body = createEmailBody();
      const payload = {
        userEmail: userDetails?.email,
        message: body,
        subject: subject,
        senderName: userDetails?.firstName,
        clientName: clientname,
      };
      await sendMailToSales(payload);
    } catch (err) {
      console.log(err);
    }
  }

  function onClose() {
    dispatch(hideSalesPopUp());
  }

  function slidePrev() {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  }

  function slideNext() {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }

  if (talktosalessuccess) {
    return (
      <TalkToSalesPop
        talktosalessuccess={talktosalessuccess}
        setTalkToSalesSuccess={() => onClose()}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        className="font-geist relative w-full max-w-[1140px] max-h-[calc(100vh-2rem)] bg-general-background rounded-lg border border-general-border shadow-lg flex flex-row overflow-y-auto app-scrollbar"
        id="modal"
      >
        <div className="w-[570px] shrink-0 flex flex-col gap-6 px-6 py-6 text-left">
          <span className="text-general-foreground text-2xl font-semibold leading-8">
            {salesContent?.heading}
          </span>
          <p className="text-unofficial-ghost-foreground text-sm font-normal leading-5">
            {salesContent?.description}
          </p>
          {salesContent?.["sub-description"] && (
            <p className="text-general-foreground text-sm font-semibold leading-5">
              {salesContent["sub-description"]}
            </p>
          )}
          <div className="flex flex-col gap-4">
            {features.map((cardText, index) => (
              <div key={index} className="flex gap-3">
                <LuCheck className="size-4 shrink-0 text-general-primary mt-0.5" />
                <div className="flex flex-col gap-0.5">
                  <p className="text-general-foreground text-sm font-semibold leading-5">
                    {cardText.split(":")[0]}
                  </p>
                  <p className="text-general-muted-foreground text-sm font-normal leading-5">
                    {cardText.split(":")[1]}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {salesContent?.note && (
            <p className="text-general-muted-foreground text-sm font-normal italic leading-5">
              {salesContent.note}
            </p>
          )}
          <button
            type="button"
            onClick={onContactSales}
            className="self-start flex items-center justify-center min-h-9 px-4 py-2 rounded-lg bg-general-primary text-general-primary-foreground text-sm font-medium hover:opacity-90 ani"
          >
            Request Access
          </button>
        </div>

        <div className="flex-1 min-w-0 flex flex-col items-center justify-center gap-3 py-6">
          <div className="flex items-center gap-1">
            {images.length > 1 && (
              <button
                type="button"
                onClick={slidePrev}
                className="group flex size-4 shrink-0 items-center justify-center rounded"
              >
                <LuChevronLeft className="size-4 text-general-muted-foreground group-hover:text-general-foreground ani" />
              </button>
            )}
            <div className="relative w-[514px] h-[342px] shrink-0 rounded-lg overflow-hidden">
              {images.map((imageUrl, index) => (
                <img
                  key={index}
                  src={imageUrl}
                  alt={salesContent?.heading}
                  className={`absolute inset-0 w-full h-full object-contain ani ${
                    index === activeIndex ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
            </div>
            {images.length > 1 && (
              <button
                type="button"
                onClick={slideNext}
                className="group flex size-4 shrink-0 items-center justify-center rounded"
              >
                <LuChevronRight className="size-4 text-general-muted-foreground group-hover:text-general-foreground ani" />
              </button>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex items-center gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`size-2 rounded-full ani ${
                    index === activeIndex ? "bg-general-primary" : "bg-unofficial-border-2"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 opacity-70 hover:opacity-100 ani"
        >
          <LuX className="size-4 text-general-foreground" />
        </button>
      </div>
    </div>
  );
}

export default memo(UnSubscribedDashboard);
